import { useState } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { TextField, Button, Typography, Modal, FormControl, Select, MenuItem } from '@mui/material';
import AssignedInterviewReviewsManager from '@/components/AssignedInterviewReviewsManager';
import { InterviewTechnicalChallenge } from '@/types/api';
import {
  TechnicalChallengeStatus,
  TechnicalChallengeStatusLabel,
} from '@/types/api/interviewTechnicalChallenge';
import * as Styles from './EditTechnicalChallengeModal.styles';

interface EditTechnicalChallengeModalProps {
  isOpen: boolean;
  onClose: () => void;
  challenge: InterviewTechnicalChallenge;
  onSave: (updatedChallenge: Partial<InterviewTechnicalChallenge>) => void;
  onReviewersChanged?: () => void;
  isLoading?: boolean;
}

const EditTechnicalChallengeModal = ({
  isOpen,
  onClose,
  challenge,
  onSave,
  onReviewersChanged,
  isLoading = false,
}: EditTechnicalChallengeModalProps): JSX.Element => {
  const [gitlabProjectUrl, setGitlabProjectUrl] = useState(challenge.gitlabProjectUrl || '');
  const [status, setStatus] = useState<TechnicalChallengeStatus>(
    challenge.status || TechnicalChallengeStatus.OPENED
  );

  const handleSave = (): void => {
    onSave({
      gitlabProjectUrl,
      status,
    });
  };

  const handleClose = (): void => {
    setGitlabProjectUrl(challenge.gitlabProjectUrl || '');
    setStatus(challenge.status || TechnicalChallengeStatus.OPENED);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="edit-technical-challenge-modal"
      aria-describedby="edit-technical-challenge-modal-description"
    >
      <Styles.ModalContainer>
        <Styles.ModalHeader>
          <Typography variant="h6" component="h2">
            Edit Technical Challenge
          </Typography>
          <Button
            onClick={handleClose}
            sx={{ minWidth: 'auto', p: 1 }}
            data-testid="close-modal-button"
          >
            <CloseIcon />
          </Button>
        </Styles.ModalHeader>

        <Styles.ModalContent>
          <Styles.FormField>
            <Typography variant="subtitle1" component="label" gutterBottom>
              GitLab Project URL
            </Typography>
            <TextField
              fullWidth
              value={gitlabProjectUrl}
              onChange={(e) => setGitlabProjectUrl(e.target.value)}
              placeholder="https://gitlab.com/username/project"
              variant="outlined"
              size="small"
              data-testid="gitlab-url-input"
            />
          </Styles.FormField>

          <Styles.FormField>
            <Typography variant="subtitle1" component="label" gutterBottom>
              Status
            </Typography>
            <FormControl size="small" sx={{ minWidth: 140, width: 'fit-content' }}>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value as TechnicalChallengeStatus)}
                data-testid="status-select"
              >
                {Object.values(TechnicalChallengeStatus).map((statusValue) => (
                  <MenuItem key={statusValue} value={statusValue}>
                    {TechnicalChallengeStatusLabel[statusValue]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Styles.FormField>

          <Styles.FormField>
            {challenge.technicalChallenge && (
              <AssignedInterviewReviewsManager
                isTeamInterview={false}
                interviewTechnicalChallengeId={challenge.id}
                selectedTechnicalChallengeId={challenge.technicalChallenge.id}
                onReviewersChanged={onReviewersChanged}
              />
            )}
          </Styles.FormField>
        </Styles.ModalContent>

        <Styles.ModalFooter>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isLoading}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isLoading}
            data-testid="save-button"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Styles.ModalFooter>
      </Styles.ModalContainer>
    </Modal>
  );
};

export default EditTechnicalChallengeModal;
